import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/**
 * OwnerRealizationSection — "C'est de ma faute"
 * Moment de vérité brutale + pivot positif.
 * SVG silhouette inline (sera remplacée par avatar Pixar généré quand budget rechargé).
 */
const OwnerRealizationSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section
      ref={ref}
      className="relative px-4 sm:px-6 py-16 sm:py-20 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)',
      }}
      data-testid="owner-realization-section"
    >
      {/* Background grain texture */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 50%, rgba(239,68,68,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(240,90,26,0.10) 0%, transparent 50%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-4xl mx-auto">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-3">
            💡 Le moment de vérité
          </p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
            La vraie raison<br/>
            n'est pas <span className="text-white/40 line-through">le marché</span><br/>
            <span className="text-[#F05A1A]">c'est le patron.</span>
          </h2>
        </motion.div>

        {/* MAIN BLOCK : Avatar + Bulle */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center mb-12">
          {/* AVATAR — silhouette SVG stylée (placeholder pour avatar Pixar) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 flex justify-center"
          >
            <div className="relative w-full max-w-[280px] aspect-square">
              {/* Halo glow rouge */}
              <div
                className="absolute inset-0 rounded-full blur-3xl"
                style={{ background: 'radial-gradient(circle, rgba(239,68,68,0.25) 0%, transparent 70%)' }}
              />

              {/* Image: si l'avatar Pixar généré existe, l'utiliser. Sinon, fallback SVG silhouette */}
              <img
                src="/restaurant-owner-realization.png"
                alt="Patron qui prend conscience"
                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'block'; }}
                className="relative w-full h-full object-cover rounded-3xl shadow-[0_20px_60px_rgba(239,68,68,0.30)]"
              />

              {/* SVG silhouette fallback */}
              <svg
                viewBox="0 0 200 200"
                className="relative w-full h-full"
                style={{ display: 'none' }}
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="owner-bg" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#3a1818" />
                    <stop offset="100%" stopColor="#1a0a0a" />
                  </linearGradient>
                  <radialGradient id="owner-spotlight" cx="50%" cy="40%" r="60%">
                    <stop offset="0%" stopColor="rgba(255,180,120,0.20)" />
                    <stop offset="100%" stopColor="transparent" />
                  </radialGradient>
                </defs>
                {/* Frame */}
                <rect width="200" height="200" rx="24" fill="url(#owner-bg)" />
                <rect width="200" height="200" rx="24" fill="url(#owner-spotlight)" />
                {/* Body silhouette */}
                <g fill="#0a0505" opacity="0.92">
                  {/* Torso (chef jacket shape) */}
                  <path d="M 60 200 Q 60 145 78 130 L 122 130 Q 140 145 140 200 Z" />
                  {/* Collar */}
                  <path d="M 88 130 L 100 145 L 112 130 Z" fill="#2a1010" />
                </g>
                {/* Arms going up to head */}
                <g fill="#0a0505" opacity="0.92">
                  <path d="M 60 145 Q 45 115 55 85 Q 60 80 70 85 Q 75 100 78 130 Z" />
                  <path d="M 140 145 Q 155 115 145 85 Q 140 80 130 85 Q 125 100 122 130 Z" />
                </g>
                {/* Hands on head */}
                <ellipse cx="70" cy="80" rx="10" ry="12" fill="#1f1212" />
                <ellipse cx="130" cy="80" rx="10" ry="12" fill="#1f1212" />
                {/* Head */}
                <ellipse cx="100" cy="90" rx="28" ry="32" fill="#1f1212" />
                {/* Eyes (downcast) */}
                <ellipse cx="90" cy="92" rx="2.5" ry="1.3" fill="#fca5a5" opacity="0.5" />
                <ellipse cx="110" cy="92" rx="2.5" ry="1.3" fill="#fca5a5" opacity="0.5" />
                {/* Chef hat (small) */}
                <path d="M 80 62 Q 85 50 100 50 Q 115 50 120 62 L 118 70 L 82 70 Z" fill="#fef3c7" opacity="0.85" />
                <rect x="78" y="68" width="44" height="5" rx="2" fill="#e0d6b8" />
                {/* Caption */}
                <text x="100" y="195" textAnchor="middle" fill="#ef4444" fontSize="9" fontWeight="900" opacity="0.6">
                  PATRON · MOMENT DE VÉRITÉ
                </text>
              </svg>

              {/* Small badge bottom */}
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-red-500/95 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                Le restaurateur qui réalise
              </div>
            </div>
          </motion.div>

          {/* BULLE DE DIALOGUE + texte */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="lg:col-span-7"
          >
            {/* Bulle de dialogue stylée */}
            <div className="relative bg-white rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.4)] mb-6">
              {/* Tail pointing left toward avatar (desktop only) */}
              <div className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 w-6 h-6 bg-white rotate-45 shadow-[-3px_3px_8px_rgba(0,0,0,0.1)]" />

              <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-3 flex items-center gap-2">
                <span className="inline-flex w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                Prise de conscience
              </p>
              <p className="font-bebas text-5xl sm:text-6xl lg:text-7xl text-[#1a1a1a] leading-[0.9] mb-4">
                "C'est de<br/>
                <span className="text-red-500">ma faute.</span>"
              </p>
              <p className="text-[#1a1a1a]/70 text-sm leading-relaxed italic mb-4">
                — Ce que tous les patrons réalisent. Trop tard.
              </p>

              {/* Micro-CTA personnel — déclenche l'action immédiate */}
              <a
                href="/diagnostic"
                className="group inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#F05A1A] text-white px-4 py-2.5 rounded-xl text-sm font-bold transition-all shadow-md"
                data-testid="owner-realization-cta"
              >
                <span>Et toi, t'en es où ? Diagnostic 5 min</span>
                <span className="transform transition-transform group-hover:translate-x-1">→</span>
              </a>
            </div>

            {/* Développé — ton posé/leçon · stagger reveal */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              variants={{
                hidden: { opacity: 1 },
                visible: { opacity: 1, transition: { staggerChildren: 0.45, delayChildren: 0.15 } },
              }}
              className="space-y-4 text-white/80 text-[15px] leading-relaxed"
              data-testid="owner-realization-prose"
            >
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
                  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: 'easeOut' } },
                }}
              >
                <span className="text-white font-bold">Au fond, ce n'est jamais le marché.</span>{' '}
                Ce n'est pas non plus la concurrence, ni le COVID, ni les charges,
                ni le serveur qui démissionne.
              </motion.p>
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
                  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: 'easeOut' } },
                }}
              >
                C'est toujours <span className="text-[#F05A1A] font-bold">le dirigeant</span> qui
                n'a pas voulu investir dans ses compétences{' '}
                <span className="text-white font-bold">dès le départ</span>. Qui a cru qu'il
                suffisait de savoir cuisiner pour réussir. Qui a refusé de se faire accompagner
                — par fierté, par méconnaissance, par peur de "perdre du temps".
              </motion.p>
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 14, filter: 'blur(4px)' },
                  visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.7, ease: 'easeOut' } },
                }}
              >
                Et quand t'as pas investi dans ton resto dès le départ, quand t'as pas
                <span className="text-white font-bold"> processé ton métier comme un industriel</span>,
                <span className="text-red-400 font-bold"> 18 mois plus tard, c'est la fermeture.</span>{' '}
                Pas par malchance. Par accumulation de petites décisions évitables.
              </motion.p>
            </motion.div>
          </motion.div>
        </div>

        {/* PIVOT POSITIF — Carte verte */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="rounded-2xl p-6 sm:p-8 border-2 border-green-500/50 shadow-[0_8px_32px_rgba(16,185,129,0.20)]"
          style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(5,150,105,0.05) 100%)' }}
        >
          <div className="flex items-start gap-4 sm:gap-6">
            <div
              className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl shadow-lg"
              style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
            >
              ✓
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-green-400 mb-2">
                Ce qu'on installe avec toi
              </p>
              <p className="font-bebas text-2xl sm:text-3xl text-white leading-tight mb-3">
                Les <span className="text-green-400">compétences de dirigeant industriel</span> qu'on n'apprend nulle part
              </p>
              <p className="text-white/75 text-sm leading-relaxed">
                Pilotage chiffré · process documentés · management par KPI · structuration RH ·
                gestion du temps · culture de la performance. Tout ce que les écoles de cuisine
                ne t'ont jamais appris — et qui décide à <span className="text-white font-bold">100%</span> si
                tu fais partie des restos qui durent ou de ceux qui ferment.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OwnerRealizationSection;
